"use client";

import { useState } from "react";
import { sendGAEvent } from "@next/third-parties/google";
import { z } from "zod";

import { Button } from "@shared/ui/button";
import { useAppForm } from "@shared/ui/form";
import { Input } from "@shared/ui/input";
import { toast } from "@shared/ui/toast";
import { cn } from "@shared/ui/utils";

import { api } from "~/trpc/client";

const schema = z.object({
  email: z.string().email("Invalid email format").max(100, { message: "Email cannot be empty" }),
});

export const JoinWaitListForm = () => {
  const [isSuccess, setIsSuccess] = useState(false);
  const { mutate, isPending } = api.identity.public.joinWaitlist.useMutation({
    onSuccess: () => {
      setIsSuccess(true);
      toast.success("Successfully joined waitlist!");
      form.reset();
    },
    onError: () => {
      toast.error("Failed to join waitlist");
    },
  });
  const form = useAppForm({
    defaultValues: { email: "" } as z.infer<typeof schema>,
    validators: { onSubmit: schema },
    onSubmitInvalid: () => {
      setIsSuccess(false);
    },
    onSubmit: ({ value }) => {
      setIsSuccess(false);
      sendGAEvent("event", "buttonClicked", { value: "test" });
      mutate(value);
    },
  });

  return (
    <form.AppForm>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
      >
        <form.AppField
          name="email"
          children={(field) => {
            const err = field.state.meta.errors[0]?.message;
            return (
              <>
                <label
                  className={cn(
                    "relative flex h-12 w-[350px] gap-x-4 rounded-xl border bg-[#354458] px-[5px] py-1 text-sm font-normal outline-offset-2 focus-within:bg-[#485567] focus-within:outline md:w-[410px]",
                  )}
                  style={{
                    borderColor: "rgba(255, 255, 255, 0.20)",
                  }}
                >
                  <Input
                    placeholder="Your Email"
                    className="placeholder:text-input-controls-secondary h-full border-0 bg-transparent px-2 focus:outline-none"
                    value={field.state.value}
                    onChange={(e) => {
                      setIsSuccess(false);
                      field.handleChange(e.target.value);
                    }}
                    disabled={isPending}
                  />
                  <Button className="h-full rounded-lg" size="sm" disabled={isPending}>
                    Join Waitlist
                  </Button>
                </label>
                <div className="mt-2 h-4 text-center text-xs font-medium">
                  {err && <span className="text-input-controls-error">{err}</span>}
                  {isPending && <span className="text-input-controls-secondary">Joining...</span>}
                  {isSuccess && <span className="text-static-success">You’ve been added to the waitlist</span>}
                </div>
              </>
            );
          }}
        />
      </form>
    </form.AppForm>
  );
};
